import { useState } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Label, Radio, Text } from '@iwoca/orion-ui';
import { FormattedMessage, useIntl } from 'react-intl';

import { getLoanDurationInMonths } from '../../../../../myOffers/MyOffers.helpers';
import { ChooseDuration } from '../ChooseDuration';
import { formatDurations } from '../formatDurations';

export const LoanExtensionRadioGroup = ({
    offers,
    setSelectedOffer,
    outstandingBalance
}: {
    offers: TFormalOffer[];
    setSelectedOffer: (offer: TFormalOffer | null) => void;
    outstandingBalance: number;
}) => {
    const [selectedOption, setSelectedOption] = useState<
        'interest_only' | 'fresh_duration' | null
    >(null);

    const interestOnlyOffer = offers.find((offer) =>
        getIsInterestOnlyOffer(offer)
    );

    const freshDurationOffers = offers.filter(
        (offer) =>
            offer.is_repayment_reduction && !getIsInterestOnlyOffer(offer)
    );

    const chooseInterestOnly = (interestOnlyFormalOffer: TFormalOffer) => {
        setSelectedOption('interest_only');
        setSelectedOffer(interestOnlyFormalOffer);
    };

    const chooseFreshDuration = (freshDurationFormalOffers: TFormalOffer[]) => {
        setSelectedOption('fresh_duration');

        if (freshDurationFormalOffers.length > 1) {
            setSelectedOffer(null);
            return;
        }

        setSelectedOffer(freshDurationFormalOffers[0]);
    };

    if (interestOnlyOffer)
        return (
            <div className="space-y-xl">
                <div className="space-y-m">
                    <Text className="text-xl font-bold">
                        <FormattedMessage
                            id="_.topup_choice.how_would_you_like_to_extend_your_loan"
                            defaultMessage="How would you like to extend your loan?"
                        />
                    </Text>
                    <div className="flex flex-col gap-m min-sm:flex-row">
                        <InterestOnlyRadioChoice
                            interestOnlyOffer={interestOnlyOffer}
                            setSelectedOffer={chooseInterestOnly}
                        />
                        <FreshDurationRadioChoice
                            freshDurationOffers={freshDurationOffers}
                            chooseFreshDuration={chooseFreshDuration}
                            outstandingBalance={outstandingBalance}
                        />
                    </div>
                </div>

                {selectedOption === 'fresh_duration' &&
                freshDurationOffers.length > 1 ? (
                    <ChooseDuration
                        offers={freshDurationOffers}
                        setSelectedOffer={setSelectedOffer}
                    />
                ) : null}
            </div>
        );

    return (
        <ChooseDuration
            offers={freshDurationOffers}
            setSelectedOffer={setSelectedOffer}
        />
    );
};

const InterestOnlyRadioChoice = ({
    interestOnlyOffer,
    setSelectedOffer
}: {
    interestOnlyOffer: TFormalOffer;
    setSelectedOffer: (formalOffer: TFormalOffer) => void;
}) => {
    return (
        <Radio.Frame className="flex-1 items-start">
            <Label htmlFor="interest-only" data-testid="interest-only-label">
                <FormattedMessage
                    id="_.topup_choice.add_3_months_reduced_repayment_period"
                    defaultMessage="Add a 3 month reduced repayment period"
                />
                <span className="block text-s font-reg">
                    <FormattedMessage
                        id="_.topup_choice.add_3_months_reduced_repayment_period_description"
                        defaultMessage="Pay only interest for 3 months, then repay your balance over {loanDuration} months"
                        values={{
                            loanDuration:
                                getLoanDurationInMonths(
                                    interestOnlyOffer!.duration!
                                ) - 3
                        }}
                    />
                </span>
            </Label>
            <Radio
                name="topup-choice"
                id="interest-only"
                className="mt-s"
                onChange={() => setSelectedOffer(interestOnlyOffer)}
            />
        </Radio.Frame>
    );
};

const FreshDurationRadioChoice = ({
    freshDurationOffers,
    chooseFreshDuration,
    outstandingBalance
}: {
    freshDurationOffers: TFormalOffer[];
    chooseFreshDuration: (formalOffer: TFormalOffer[]) => void;
    outstandingBalance: number;
}) => {
    const intl = useIntl();

    return (
        <Radio.Frame className="flex-1 items-start">
            <Label
                htmlFor="reduce-minimum-repayments"
                data-testid="fresh-duration-label"
            >
                <FormattedMessage
                    id="_.topup_choice.fresh_new_duration"
                    defaultMessage="Fresh new duration starting from today"
                />
                <span className="block text-s font-reg">
                    <FormattedMessage
                        id="_.topup_choice.fresh_new_duration_description"
                        defaultMessage="Start a new agreement for {durations} months whilst keeping your {outstandingBalance} balance."
                        values={{
                            durations: formatDurations(
                                freshDurationOffers,
                                // @ts-expect-error intl.locale is typed as string
                                intl.locale
                            ),
                            outstandingBalance: (
                                <FormattedCurrency value={outstandingBalance} />
                            )
                        }}
                    />
                </span>
            </Label>
            <Radio
                name="topup-choice"
                id="reduce-minimum-repayments"
                className="mt-s"
                onChange={() => chooseFreshDuration(freshDurationOffers)}
            />
        </Radio.Frame>
    );
};
